'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Owner-only modal for handing off a club to another existing member.
 *
 * Why "existing member" not "invite a new person":
 *   The transfer should never be the first time the new owner shows up
 *   in the system — too easy to typo a UID and hand off control to a
 *   stranger. They have to be in the club's member list already (added
 *   via the regular invite flow). The modal hides the current owner
 *   from the picker so you can't transfer to yourself by mistake.
 *
 * UX:
 *   • Pick a member from a radio list (members passed in by parent —
 *     fetched via useClub already, no extra query)
 *   • Two-step confirm: pick → type the club name to confirm
 *   • Calls transferClubOwnership Cloud Function
 *   • On success: toast + onTransferred() callback (parent reloads)
 *
 * Props:
 *   open, onClose, club: { id, name }, members: [{ uid, displayName, email, role }],
 *   currentOwnerUid, onTransferred()
 */
export default function TransferOwnershipModal({
  open,
  onClose,
  club,
  members = [],
  currentOwnerUid,
  onTransferred,
}) {
  const [selectedUid, setSelectedUid] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal opens for a fresh hand-off
  useEffect(() => {
    if (open) {
      setSelectedUid(null);
      setConfirmText('');
      setSubmitting(false);
    }
  }, [open]);

  // Hide the current owner from the picker — they can't hand off to
  // themselves, and showing them as a target is confusing.
  const candidates = useMemo(
    () => (members || [])
      .filter((m) => m && m.uid && m.uid !== currentOwnerUid)
      .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')),
    [members, currentOwnerUid]
  );

  if (!open) return null;

  const selected = candidates.find((m) => m.uid === selectedUid) || null;
  const expectedConfirm = club?.name || '';
  const confirmMatches = expectedConfirm && confirmText.trim() === expectedConfirm.trim();
  const canSubmit = !!selected && confirmMatches && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fn = httpsCallable(getFunctions(), 'transferClubOwnership');
      await fn({ clubId: club.id, newOwnerUid: selected.uid });
      toast.success(`Ownership transferred to ${selected.displayName || selected.email || 'new owner'}.`);
      if (typeof onTransferred === 'function') onTransferred(selected.uid);
      onClose();
    } catch (err) {
      console.error('[TransferOwnershipModal] transfer failed', err);
      const msg = err?.message || 'Failed to transfer ownership.';
      const code = err?.code || '';
      if (code === 'functions/permission-denied') {
        toast.error('Only the current club owner can transfer ownership.');
      } else if (code === 'functions/failed-precondition') {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-zinc-100 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
            </div>
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-amber-700"
                style={{ fontFamily: HEAD_FONT }}
              >
                Transfer ownership
              </p>
              <h2
                className="uppercase text-[#0d1f14] mt-0.5"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20 }}
              >
                {club?.name || 'this club'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 -m-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-zinc-700">
            The new owner takes over full control. You'll be demoted to <span className="font-semibold text-[#0d1f14]">Club Admin</span> — you keep access but they call the shots from here on.
          </p>

          {/* Member picker */}
          {candidates.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800">No one to transfer to</p>
              <p className="text-xs text-amber-700 mt-1">
                You're the only member of this club. Invite another coach or admin first, then come back here to transfer.
              </p>
            </div>
          ) : (
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500 mb-2"
                style={{ fontFamily: HEAD_FONT }}
              >
                Pick the new owner
              </p>
              <ul className="space-y-2">
                {candidates.map((m) => {
                  const checked = selectedUid === m.uid;
                  return (
                    <li key={m.uid}>
                      <label
                        className={`flex items-center gap-3 rounded-xl border px-3 py-3 cursor-pointer transition-colors ${
                          checked
                            ? 'border-[#b5f03d]/40 bg-[#b5f03d]/5'
                            : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
                        }`}
                      >
                        <input
                          type="radio"
                          name="newOwner"
                          checked={checked}
                          onChange={() => setSelectedUid(m.uid)}
                          disabled={submitting}
                          className="h-4 w-4 border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                        />
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                          <UserCircleIcon className="h-5 w-5 text-[#5a8a1f]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0d1f14] truncate">
                            {m.displayName || m.email || 'Member'}
                          </p>
                          <p className="text-xs text-zinc-500 truncate">
                            {m.email && m.displayName ? m.email : m.role || 'Member'}
                          </p>
                        </div>
                        {checked && (
                          <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f] shrink-0" />
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Confirm-by-typing — destructive-action UX. They must type
              the club name verbatim before the Transfer button enables.
              Same pattern as the existing delete-club confirmation in
              ClubSettings; users already know what to expect. */}
          {selected && (
            <div className="space-y-2">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                style={{ fontFamily: HEAD_FONT }}
              >
                To confirm, type <span className="text-[#0d1f14]">{expectedConfirm}</span>
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={submitting}
                placeholder={expectedConfirm}
                className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 transition"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all disabled:opacity-50"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Transferring…
              </>
            ) : (
              'Transfer ownership'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
